import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EyeIcon } from '../../assets/icons/eye-icon.svg';
import { ReactComponent as EyeIconSlash } from '../../assets/icons/eye-slash.svg';
import { TooltipIcon } from '../tooltip/TooltipIcon';

type InputType = 'text' | 'number' | 'email' | 'password';

interface CustomInputProperties {
  inputLabel?: string;
  type: InputType;
  placeholder: string;
  value: string | number | null;
  name: string;
  onChangeValue?: (name: string, value: string | number) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  svgIcon?: JSX.Element;
  frontSvgIcon?: JSX.Element;
  svgIconClickFunc?: () => void;
  error?: boolean;
  errorMessage?: string;
  handleBlur?: (name: string) => void;
  autoFocus?: boolean;
  fontSize?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  hint?: boolean;
  hintMessage?: string;
  requiredTooltip?: boolean;
  tooltipText?: string;
  showTooltipForce?: boolean;
  characterLimit?: number;
  maxValue?: number;
  decimalLimit?: number;
  limitErrorMsg?: string;
  charactersOnly?: boolean;
  suffixText?: string;
}

/**
 * Custom Input box function component
 * @param {any} param0 - The param0
 * @param {any} param0.inputLabel - The inputLabel prop
 * @param {any} param0.type - The type prop
 * @param {any} param0.name - The name prop
 * @param {any} param0.placeholder - The placeholder prop
 * @param {any} param0.value - The value prop
 * @param {any} param0.onChangeValue - The onChangeValue prop
 * @param {any} param0.valueRequired - The valueRequired prop
 * @param {any} param0.readOnly - The readOnly prop
 * @param {any} param0.svgIcon - The svgIcon prop
 * @param {any} param0.frontSvgIcon - The frontSvgIcon prop
 * @param {any} param0.svgIconClickFunc - The svgIconClickFunc prop
 * @param {any} param0.error - The error prop
 * @param {any} param0.errorMessage - The errorMessage prop
 * @param {any} param0.handleBlur - The handleBlur prop
 * @param {any} param0.autoFocus - The autoFocus prop
 * @param {any} param0.fontSize - The fontSize prop
 * @param {any} param0.disabled - The disabled prop
 * @param {any} param0.hint - The hint prop
 * @param {any} param0.hintMessage - The hintMessage prop
 * @param {any} param0.requiredTooltip - The requiredTooltip prop
 * @param {any} param0.tooltipText - The tooltipText prop
 * @param {any} param0.showTooltipForce - The showTooltipForce prop
 * @param {any} param0.characterLimit - The characterLimit prop
 * @param {any} param0.maxValue - The maxValue prop
 * @param {any} param0.decimalLimit - The decimalLimit prop
 * @param {any} param0.limitErrorMsg - The limitErrorMsg prop
 * @param {any} param0.charactersOnly - Restrict input to letters (and spaces) only, no numbers/symbols
 */
function CustomInput({
  inputLabel,
  type,
  name,
  placeholder,
  value,
  onChangeValue = () => {},
  valueRequired,
  readOnly,
  svgIcon,
  frontSvgIcon,
  svgIconClickFunc,
  error,
  errorMessage,
  handleBlur,
  autoFocus,
  fontSize = 'large',
  disabled,
  hint,
  hintMessage,
  requiredTooltip,
  tooltipText,
  showTooltipForce = false,
  characterLimit,
  maxValue,
  decimalLimit,
  limitErrorMsg,
  charactersOnly,
  suffixText,
}: Readonly<CustomInputProperties>) {
  const { t } = useTranslation('sharedComponent');
  const inputReference = useRef<HTMLInputElement>(null);
  const [inputType, setInputType] = useState<InputType>(type);
  const [limitError, setLimitError] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const timeoutReference = useRef<NodeJS.Timeout | null>(null);

  /**
   * Displays a limit validation error message and automatically
   * clears it after a specified timeout.
   *
   * If a previous timeout exists, it will be cleared before
   * creating a new one to prevent multiple timers from running.
   * @param {string} message - The validation error message to display.
   */
  const showLimitError = (message: string) => {
    setLimitError(message);

    if (timeoutReference.current) {
      clearTimeout(timeoutReference.current);
    }

    timeoutReference.current = setTimeout(() => {
      setLimitError('');
    }, 3000); // 3 seconds
  };

  /**
   * Resets the native input's DOM value back to the last valid controlled
   * value. Needed because rejecting a keystroke via early return doesn't
   * change parent state, so React never resyncs the DOM on its own.
   */
  const resetToLastValidValue = () => {
    if (inputReference.current) {
      inputReference.current.value = value === null || value === undefined ? '' : String(value);
    }
  };

  /**
   * Input box change handle function
   * @param {any} changeValue - The changeValue
   */
  const handleValueChange = (changeValue: string | number) => {
    let updatedValue = changeValue;
    if (type === 'email' && typeof changeValue === 'string') {
      updatedValue = changeValue.toLowerCase();
    }
    if (charactersOnly && typeof updatedValue === 'string') {
      updatedValue = updatedValue.replace(/[^a-zA-Z\s]/g, '');
    }
    const stringValue = String(updatedValue);

    // Text field validation
    if (type !== 'number' && characterLimit && stringValue.length < characterLimit) {
      setLimitError('');
    }

    // Number field decimal-limit validation
    if (type === 'number' && decimalLimit !== undefined) {
      const decimalPattern = new RegExp(`^-?\\d*\\.?\\d{0,${decimalLimit}}$`);
      if (!decimalPattern.test(stringValue)) {
        resetToLastValidValue();
        return; // prevent update, keep last valid value
      }
    }

    // Number field validation
    if (type === 'number' && maxValue !== undefined) {
      const numericValue = Number(updatedValue);

      if (numericValue > maxValue) {
        showLimitError(limitErrorMsg || `Maximum value allowed is ${maxValue}.`);
        resetToLastValidValue();
        return; // prevent update
      }

      setLimitError('');
    }

    onChangeValue(name, updatedValue);
  };

  /**
   * Blocks further typing once characterLimit reached and shows limit error.
   * Native maxLength stops the char before onChange fires, so overflow must be
   * caught here to trigger the message only on the attempted extra keystroke.
   * @param {any} event - The react keyboard event from the input field
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (type === 'number' || !characterLimit) return;

    const isControlKey =
      event.ctrlKey ||
      event.metaKey ||
      event.key.length > 1 ||
      ['Backspace', 'Delete'].includes(event.key);

    const { selectionStart, selectionEnd, value: currentValue } = event.currentTarget;
    const hasSelection = selectionStart !== selectionEnd;

    if (!isControlKey && !hasSelection && currentValue.length >= characterLimit) {
      showLimitError(limitErrorMsg || `Maximum ${characterLimit} characters allowed.`);
    }
  };

  /**
   * Input's start and end white space trim helper
   * @param {any} event - The react focus event from the input field
   */
  const internalHandleBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    const rawValue = event.target.value;

    // Trim start & end only
    let trimmedValue = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    if (type === 'email' && typeof trimmedValue === 'string') {
      trimmedValue = trimmedValue.toLowerCase();
    }

    // If different, update parent
    if (trimmedValue !== rawValue) {
      onChangeValue(name, trimmedValue);
    }

    setIsFocused(false);

    // Call parent blur callback if provided
    if (handleBlur) {
      handleBlur(name);
    }
  };

  useEffect(() => {
    if (autoFocus && inputReference.current) {
      inputReference.current.focus(); // Focus the input field if condition is true
    }
  }, [autoFocus]);

  useEffect(() => {
    return () => {
      if (timeoutReference.current) {
        clearTimeout(timeoutReference.current);
      }
    };
  }, []);

  return (
    <div className={`custom-input-wrap ${fontSize} ${disabled ? 'disabled' : ''}`}>
      {inputLabel && (
        <p className={`input-label ${error && 'error'}`}>
          {t(inputLabel, { nsSeparator: false })}
          {valueRequired && <span>*</span>}
          {requiredTooltip && tooltipText && (
            <TooltipIcon
              text={t(tooltipText, { nsSeparator: false })}
              showTooltipForce={showTooltipForce}
            />
          )}
        </p>
      )}
      <div
        className={`custom-input-container ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}>
        {frontSvgIcon && <span>{frontSvgIcon}</span>}
        <input
          ref={inputReference}
          type={type === 'password' ? inputType : type}
          placeholder={placeholder ? t(placeholder, { nsSeparator: false }) : ''}
          value={
            type === 'number' &&
            decimalLimit !== undefined &&
            !isFocused &&
            value !== null &&
            value !== undefined &&
            value !== '' &&
            !Number.isNaN(Number(value))
              ? Number(value).toFixed(decimalLimit)
              : (value ?? '')
          }
          onChange={(event) => handleValueChange(event.target.value)}
          onKeyDown={handleKeyDown}
          required={valueRequired}
          readOnly={readOnly}
          onFocus={() => setIsFocused(true)}
          onBlur={internalHandleBlur}
          disabled={disabled}
          onWheel={(event) => event.currentTarget.blur()}
          maxLength={characterLimit}
        />
        {suffixText && <span className="input-suffix">{suffixText}</span>}
        {svgIcon && type !== 'password' && (
          <button type="button" className="icon" onClick={svgIconClickFunc}>
            {svgIcon}
          </button>
        )}
        {(() => {
          let passwordIcon: JSX.Element | null = null; // Initialize the type as either a JSX element or null
          if (type === 'password' && inputType === 'password') {
            passwordIcon = (
              <button type="button" className="icon" onClick={() => setInputType('text')}>
                <EyeIcon />
              </button>
            );
          } else if (type === 'password' && inputType === 'text') {
            passwordIcon = (
              <button type="button" className="icon" onClick={() => setInputType('password')}>
                <EyeIconSlash />
              </button>
            );
          }
          return passwordIcon; // Return the icon or null
        })()}
      </div>
      {/* <span className="text-danger">
        {error || limitError ? limitError || errorMessage : '\u00A0'}
      </span> */}
      {(error || limitError) && <span className="text-danger">{limitError || errorMessage}</span>}
      {hint && hintMessage && <p className="custom-input-hint-message">{hintMessage}</p>}
    </div>
  );
}

CustomInput.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  readOnly: false,
  svgIcon: null,
  frontSvgIcon: null,
  error: false,
  errorMessage: '',
  handleBlur: null,
  autoFocus: false,
  fontSize: 'large',
  disabled: false,
  hint: true,
  hintMessage: '',
  showTooltipForce: false,
  charactersOnly: false,
  /**
   * Icon click function
   */
  svgIconClickFunc: () => {},
  /**
   * Input change function
   */
  onChangeValue: () => {},
  suffixText: '',
};

export default CustomInput;
